import { useEffect, useRef, useState } from "react";
import { Questions } from "./Asks.tsx";
import { ApiError, type Ask, api, type Change, type ChangePage, type Item, type TripView } from "./api.ts";
import { examplePrompt } from "./ask-ai.ts";
import { changeLines } from "./changes.ts";
import { asLabel, everyone, FormerFace, isFormer } from "./former.tsx";
import { useKept, useKeptScroll } from "./kept.ts";
import { litProps, useLastOpened } from "./opened.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Avatar, Button, Chip, Icon } from "./souvenir.ts";
import { FirstVisit, useFirstVisitOpen } from "./Tips.tsx";
import { Empty } from "./ui.tsx";

// What's changed in the trip, newest first and by day: who (and through which AI, or a friend in their
// pad), why, and what each change did. Your own changes can be undone here; anything else, ask an AI.

interface Props {
  trip: TripView;
  // Everything in the trip, so lines about something still there open its card.
  items: Item[];
  // Questions: those waiting on me and my open ones lead the tab.
  asks: Ask[];
  onOpen: (id: string) => void;
  // After an undo: the trip changed, load it again.
  onChanged: () => void;
}

const SHOWN_LINES = 5;

export function Updates({ trip, items, asks, onOpen, onChanged }: Props) {
  const [pages, setPages] = useState<ChangePage[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [version, setVersion] = useState(0);
  // How many pages were shown ("Show earlier"), so Back finds the feed as long as it was.
  const [shownPages, setShownPages] = useKept("updates-pages", 1);
  const list = useRef<HTMLElement>(null);
  const tipOpen = useFirstVisitOpen("updates");
  const readOnly = useReadOnly();
  useKeptScroll(list, "updates");
  // The last thing opened from here, lit where it was opened from: the same thing may be in many changes,
  // so which line or question it was (`at`) is kept with it.
  const last = useLastOpened();
  const [openedAt, setOpenedAt] = useKept<string | null>("updates-opened-at", null);
  const openAt = (at: string, id: string) => {
    setOpenedAt(at);
    onOpen(id);
  };
  const litAt = (at: string, id: string) => id === last && (openedAt === null || openedAt === at);

  // biome-ignore lint/correctness/useExhaustiveDependencies: asks for the feed again after an undo (version) or a refresh (last_change), as long as it was
  useEffect(() => {
    let live = true;
    (async () => {
      const got = [await api.changes(trip.id)];
      for (let cursor = got[0]?.next_cursor; live && cursor && got.length < shownPages; ) {
        const page = await api.changes(trip.id, cursor);
        got.push(page);
        cursor = page.next_cursor;
      }
      if (live) setPages(got);
    })().catch((e: Error) => live && setFailed(e.message));
    return () => {
      live = false;
    };
  }, [trip.id, version, trip.last_change]);

  const byId = new Map(items.map((i) => [i.id, i]));
  const ctx = { items: byId, members: everyone(trip), you: trip.you };
  const all = pages?.flatMap((p) => p.changes) ?? [];
  const next = pages?.at(-1)?.next_cursor ?? null;

  const more = async () => {
    if (!next) return;
    setLoadingMore(true);
    try {
      const page = await api.changes(trip.id, next);
      setPages((p) => [...(p ?? []), page]);
      setShownPages((pages?.length ?? 1) + 1);
    } catch (e) {
      setFailed((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="tm-updates" aria-labelledby="updates-title" aria-busy={pages === null} ref={list}>
      <header className="tm-updates-head">
        <h1 id="updates-title" className="display-m">
          Updates
        </h1>
        {/* While the first-visit card shows, it stands in for this line. */}
        {!tipOpen && (
          <p className="body tm-muted">
            {readOnly
              ? "What's changed, newest first: who, through which AI, and why."
              : "Questions waiting on you, then what's changed, newest first: who, through which AI, and why. Undo your own changes here, or ask your AI."}
          </p>
        )}
      </header>
      <FirstVisit view="updates" />
      <Questions asks={asks} trip={trip} onOpen={openAt} onChanged={onChanged} lit={litAt} />
      {failed && (
        <p className="callout tm-error">
          {failed}
          <ReportThis />
        </p>
      )}
      {pages && all.length === 0 && (
        <Empty prompt={examplePrompt(trip.title, "catchUp")} readOnly="Nothing has changed.">
          Nothing has changed yet. Every change to the trip shows here, newest first: who made it, through which AI, and
          why.
        </Empty>
      )}
      {byDay(all).map(([day, list]) => (
        <section key={day} className="tm-updates-day" aria-label={day}>
          <h2 className="eyebrow tm-muted">{day}</h2>
          <ol className="tm-changes">
            {list.map((c) => (
              <ChangeRow
                key={c.id}
                change={c}
                trip={trip}
                lines={changeLines(c, ctx)}
                onOpen={openAt}
                lit={litAt}
                onUndone={() => {
                  setVersion((v) => v + 1);
                  onChanged();
                }}
              />
            ))}
          </ol>
        </section>
      ))}
      {next && (
        <Button variant="secondary" size="sm" className="tm-updates-more" disabled={loadingMore} onClick={more}>
          {loadingMore ? "Loading…" : "Show earlier"}
        </Button>
      )}
    </section>
  );
}

function ChangeRow({
  change,
  trip,
  lines,
  onOpen,
  lit,
  onUndone,
}: {
  change: Change;
  trip: TripView;
  lines: ReturnType<typeof changeLines>;
  // What a line is about opens from it (`at`: which line), lit when it was the last opened from here.
  onOpen: (at: string, id: string) => void;
  lit: (at: string, id: string) => boolean;
  onUndone: () => void;
}) {
  const [all, setAll] = useKept(`updates-all-${change.id}`, false);
  // Nothing is undone on a read-only trip.
  const readOnly = useReadOnly();
  const canUndo = change.can_undo && !readOnly;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const by = change.by;
  const friend = "friend" in by;
  const person = friend ? undefined : everyone(trip).find((m) => m.id === by.id);
  const name = friend ? by.friend : by.id === trip.you ? "You" : asLabel(by.name);
  const former = !friend && isFormer(person ?? by);
  const via = friend ? `in ${by.pad_name}` : change.via === "web" ? "on the web" : change.via && `via ${change.via}`;
  const shown = all ? lines : lines.slice(0, SHOWN_LINES);

  const undo = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.undo(trip.id, change.id);
      onUndone();
    } catch (e) {
      setError(undoError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="tm-change">
      {friend ? (
        <span className="tm-row-icon tm-change-face">
          <Icon name="meet" size={18} />
        </span>
      ) : former ? (
        <FormerFace size={36} className="tm-change-face" />
      ) : (
        <Avatar
          name={by.name}
          color={person?.color ?? "sky"}
          {...(person?.src ? { src: person.src } : {})}
          size={36}
          className="tm-change-face"
        />
      )}
      <div className="tm-change-body">
        <p className="tm-change-head">
          <span className="body-strong">{name}</span>
          <span className="caption tm-muted">{[via, time(change.at)].filter(Boolean).join(" · ")}</span>
        </p>
        {/* A friend's reason is Tripmo's own ("Jon wrote in …"); the head already says it. */}
        {change.reason && !friend && <p className="callout tm-change-reason">{change.reason}</p>}
        <ul className="tm-change-lines">
          {shown.map((line, n) => {
            const content = (
              <>
                <span className="tm-change-icon">
                  <Icon name={line.icon} size={16} />
                </span>
                <span>{line.text}</span>
              </>
            );
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: a change's lines are fixed, in order
              <li key={n}>
                {line.item ? (
                  <button
                    type="button"
                    className={`tm-change-line is-link${lit(`${change.id}-${n}`, line.item) ? " is-lit" : ""}`}
                    {...litProps(lit(`${change.id}-${n}`, line.item))}
                    onClick={() => onOpen(`${change.id}-${n}`, line.item as string)}
                  >
                    {content}
                  </button>
                ) : (
                  <span className="tm-change-line">{content}</span>
                )}
              </li>
            );
          })}
        </ul>
        {lines.length > SHOWN_LINES && (
          <button type="button" className="tm-link caption tm-change-all" onClick={() => setAll(!all)}>
            {all ? "Show fewer" : `${lines.length - SHOWN_LINES} more`}
          </button>
        )}
        {(change.undone_by || canUndo) && (
          <div className="tm-change-foot">
            {change.undone_by ? (
              <Chip icon="replay">Undone</Chip>
            ) : (
              <Button variant="quiet" size="sm" icon="replay" className="tm-start" disabled={busy} onClick={undo}>
                {busy ? "Undoing…" : "Undo"}
              </Button>
            )}
          </div>
        )}
        {error && (
          <p className="caption tm-error" role="alert">
            {error}
            <ReportThis />
          </p>
        )}
      </div>
    </li>
  );
}

// Why an undo was refused, for a person rather than an AI.
function undoError(e: unknown): string {
  if (e instanceof ApiError && e.status === 409)
    return "Can't undo this: something it changed has changed since. Ask your AI to put it back instead.";
  const why = (e as Error).message.match(/can't be undone: (.*?)\. Change it directly instead\.$/)?.[1];
  return why ? `Can't undo this: ${why}.` : (e as Error).message;
}

const TIME = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" });
const DAY = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short" });
const DAY_YEAR = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

const time = (at: string) => TIME.format(new Date(at));

// Changes grouped by the local day they happened: "Today", "Yesterday", "Tue 13 Oct".
function byDay(changes: Change[]): [string, Change[]][] {
  const key = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const groups = new Map<string, Change[]>();
  for (const c of changes) {
    const d = new Date(c.at);
    const label =
      key(d) === key(now)
        ? "Today"
        : key(d) === key(yesterday)
          ? "Yesterday"
          : d.getFullYear() === now.getFullYear()
            ? DAY.format(d)
            : DAY_YEAR.format(d);
    groups.set(label, [...(groups.get(label) ?? []), c]);
  }
  return [...groups];
}
